import React, { useEffect, useState } from "react";
import { Card, Row, Col, Table, Typography, Divider, Grid } from "antd";
import { getSalesOrderRecordWithDetailsApi } from "../../../../../apiservice/sales";
import { numberFormat, convertNumberToWords, formatToCompanyTimezone } from "../../../../../lib/functions";
import "./voucher-mobile.css"
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../../../Auth/authSelectors";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const SalesOrderVoucherBody = ({ sale_o_id, institution, printMode = false, showStamp = false,
  showSignature = false, setVoucherNo, setAccName, setCustomerEmail }) => {
  const [data, setData] = useState(null);
  const authInfo = useSelector(selectAuthInfo);
  const screens = useBreakpoint();
  const isMobile = !printMode && !screens.sm;

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isBonusItem = (item) => Number(item.item_rate) === 0;
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled == "no";

  useEffect(() => {
    if (!sale_o_id) return;
    getSalesOrderRecordWithDetailsApi({ sale_o_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) {
        const record = res.data[0];
        setData(record);
        // important guard for printMode
        if (!printMode && setVoucherNo) {
          setVoucherNo(record.sale_order_no);
          setAccName(record.acc_name);
        }

        if (record.email_address) {
          setCustomerEmail?.(record.email_address);
        }

      }
    });
  }, [sale_o_id]);

  if (!data) return null;

  // Real enforcement point for both Download and Print: this component
  // renders in printMode for the hidden react-to-print layout (Print) AND
  // for the /print/sales-order/:id route the backend Puppeteer service
  // screenshots (Download) — blocking the render here blocks both actions
  // regardless of button state, not just a disabled prop that could be
  // bypassed. Normal on-screen viewing (printMode=false) is untouched.
  if (printMode && data.approval_status === "pending") {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Title level={4}>Pending Approval</Title>
        <Text>This sales order is pending approval and cannot be downloaded or printed yet.</Text>
      </div>
    );
  }

  const isIndividual = data.is_cal_type === "individual";

  // normalize items and create qty_display / done_qty_display
  const items = (data.details || []).map((item) => ({
    ...item,
    qty_display:
      (item?.item_qty ?? item?.sale_o_qty ?? 0) +
      " " +
      (item?.unit_symbol || item?.per_unit_symbol || ""),
    done_qty_display:
      (item?.done_item_qty ?? 0) +
      " " +
      (item?.unit_symbol || item?.per_unit_symbol || ""),
  }));

  const hasBonusItems = items.some(isBonusItem);

  const totals = items.reduce(
    (acc, d) => {
      const qty = Number(d.item_qty ?? d.sale_o_qty ?? 0);
      const rate = Number(d.item_rate ?? 0);

      let base = qty * rate;

      const discountAmt =
        (Number(d.item_discount_per || 0) / 100) * base;

      const afterDiscount = base - discountAmt;

      const taxAmt =
        (Number(d.item_tax_per || 0) / 100) * afterDiscount;

      acc.subTotal += base;
      acc.discountTotal += discountAmt;
      acc.taxTotal += taxAmt;
      acc.grandTotal += afterDiscount + taxAmt;

      return acc;
    },
    { subTotal: 0, discountTotal: 0, taxTotal: 0, grandTotal: 0 }
  );

  const totalTax = isIndividual
    ? totals.taxTotal
    : Number(data?.total_tax || 0);

  const cgst = isIndia ? totalTax / 2 : 0;
  const sgst = isIndia ? totalTax / 2 : 0;

  // calculate grand total for individual mode
  const grandTotal = isIndividual
    ? totals.grandTotal
    : Number(data?.total_amount || 0);

  const columns = isIndividual
    ? [
      { title: "#", render: (_, __, i) => i + 1, width: 40 },
      {
        title: "Item",
        dataIndex: "item_name",
        className: "col-item",
        render: (text, record) => (
          <div className="item-cell bonus-badge-container">
            {text}
            {isBonusItem(record) && (
              <span className="bonus-badge">BONUS</span>
            )}
            {record.item_description && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 2, whiteSpace: "pre-wrap" }}>
                {record.item_description}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v || 0).toFixed(2),
        width: 50
      },
      {
        title: "Per",
        dataIndex: "per_unit_symbol",
        align: "right",
        render: (_, r) => r.per_unit_symbol || r.unit_symbol || "-",
        width: 70
      },
      {
        title: "Qty",
        dataIndex: "qty_display",
        align: "right",
        render: (v) => v || "0",
        width: 80
      },
      {
        title: "Disct%",
        dataIndex: "item_discount_per",
        align: "right",
        render: (v) => (v == null ? "0" : Number(v).toFixed(2)),
        width: 75
      },
      ...(!isGSTDisabled
        ? [
          {
            title: `${taxLabel}% `,
            dataIndex: "item_tax_per",
            align: "right",
            render: (v) => (v == null ? "0" : Number(v).toFixed(2)),
            width: 70
          },
        ]
        : []),
      {
        title: "Total",
        align: "right",
        render: (_, d) => {
          if (d.item_total == 0) {
            return Number(0).toFixed(2);
          }
          const qty = Number(d.item_qty ?? d.sale_o_qty ?? 0);
          const rate = Number(d.item_rate ?? 0);
          let total = qty * rate;
          const discountAmt = (Number(d.item_discount_per) || 0) / 100 * (qty * rate);
          total -= discountAmt;
          const taxAmt = (Number(d.item_tax_per) || 0) / 100 * (qty * rate - discountAmt);
          total += taxAmt;
          return Number(total || 0).toFixed(2);
        },
        width: 90
      },
    ]
    : [
      { title: "#", render: (_, __, i) => i + 1, width: 40 },
      {
        title: "Item",
        dataIndex: "item_name",
        className: "col-item",
        render: (text, record) => (
          <div className="item-cell bonus-badge-container">
            {text}
            {isBonusItem(record) && (
              <span className="bonus-badge">BONUS</span>
            )}
            {record.item_description && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 2, whiteSpace: "pre-wrap" }}>
                {record.item_description}
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Rate",
        dataIndex: "item_rate",
        align: "right",
        render: (v) => Number(v || 0).toFixed(2),
        width: 70
      },
      {
        title: "Per",
        dataIndex: "per_unit_symbol",
        align: "right",
        render: (_, r) => r.per_unit_symbol || r.unit_symbol || "-",
        width: 70
      },
      {
        title: "Quantity",
        dataIndex: "qty_display",
        align: "right",
        render: (v) => v || "0",
        width: 110
      },
      {
        title: "Sold Quantity",
        dataIndex: "done_qty_display",
        align: "right",
        render: (v) => v || "0",
        width: 110
      },
      {
        title: "Amount",
        dataIndex: "item_total",
        align: "right",
        render: (v) => Number(v || 0).toFixed(2),
        width: 90
      },
    ];

  return (
    <div className="voucher-section invoice-v2">
      {/* Header */}
      <Row align="top" className="invoice-header" wrap={isMobile}>
        <Col flex={isMobile ? "1 1 100%" : "0 0 320px"} style={{ marginTop: isMobile ? 0 : "66px" }}>
          <Text strong>Customer/ Debtor Code : &nbsp;{data.acc_code}</Text>
          <br />
          <Text strong>Name : &nbsp;{data.acc_name}</Text>
          {/* <br />
            <Text>Institution : &nbsp;{data.institution_name || '__'}</Text> */}
          <br />
          {data?.acc_mgr && <Text className="wrap-text" >
            {institution?.country?.trim().toLowerCase() === "united arab emirates" &&
              (institution?.subscription_type.toLowerCase().includes("pharma") || institution?.subscription_type.toLowerCase().includes("pharmabooks"))
              ? "Rep. : "
              : "Account Manager : "}{" "}
            {data?.acc_mgr}
          </Text>}
          <Text className="wrap-text">Address : &nbsp; {data.address}</Text>

          <Text>Contact No : &nbsp; {data.contact_no}</Text>
          <br />
          {data?.vat_no && <Text>
            {authInfo?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {data?.vat_no}
          </Text>}
        </Col>
        <Col flex={isMobile ? "1 1 100%" : "320px"} className="invoice-title">
          <Title level={3}>SALES ORDER</Title>
          <Text strong>Voucher No : {data.sale_order_no}</Text>
          <br />
          <Text>Order Date : {formatToCompanyTimezone(data.created_date)?.split(" ")[0]}</Text>
          <br />
          <Text>Order By : {String(data.full_name || "").split(" ")[0]}</Text>
          <br />
          <Text className="wrap-text">Address : {institution?.street_address}</Text>
          {institution?.phone_no && <Text>Contact No : &nbsp; {institution?.phone_no}</Text>}
          <br />

          {data?.customer_po_ref && <><Text>Customer Po Ref : {data.customer_po_ref}</Text>
            <br /></>}
          {data?.payment_terms && <><Text>Payment Terms : {data.payment_terms} Days</Text>
            <br /></>}

          {institution?.vat_license && <><Text>
            {institution?.country?.trim().toLowerCase() === "united arab emirates"
              ? "TRN No: "
              : "Tax Reg No: "}{" "}
            {institution?.vat_license}
          </Text><br /></>}
          {data.licence_no && <Text>License No : {data.licence_no}</Text>}
        </Col>
      </Row>


      {/* Items Table */}
      <Table
        className="invoice-items"
        dataSource={items}
        columns={columns}
        pagination={false}
        rowKey="item_id"
        size="small"
        rowClassName={(record) =>
          isBonusItem(record) ? "bonus-row" : ""
        }
        summary={() => {
          if (isIndividual) {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={isGSTDisabled ? 6 : 7} align="right">
                  <b>Grand Total</b>
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  <b>{numberFormat(grandTotal)}</b>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }


          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Subtotal
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.sub_total || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  Discount
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {Number(data.total_discount || 0).toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {/* {isIndia ? (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={5} align="right">
                      CGST
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      {cgst.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>

                  <Table.Summary.Row>
                    <Table.Summary.Cell colSpan={5} align="right">
                      SGST
                    </Table.Summary.Cell>
                    <Table.Summary.Cell align="right">
                      {sgst.toFixed(2)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              ) : (
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={5} align="right">
                    {taxLabel}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    {totalTax.toFixed(2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )} */}

              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={5} align="right">
                  {taxLabel}
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {totalTax.toFixed(2)}
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {Number(data.other_service_cost || 0) > 0 && (
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={5} align="right">
                    Additional Expense
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    {Number(data.other_service_cost || 0).toFixed(2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}

            </>
          );
        }}
      />

      {hasBonusItems && <Text style={{ display: "block", fontSize: 11, color: "#666", marginLeft: 10, marginTop: 10 }}>
        *Bonus items are supplied free of cost and not included in taxable value.
      </Text>}

      {/* Footer */}
      <Divider />
      <Row>
        <Col span={16} />
        <Col span={8} style={{ textAlign: "right" }}>
          <div>
            {/* Subtotal */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Sub Total :</Text>
              <Text>
                {numberFormat(isIndividual ? totals.subTotal : data.sub_total)}
              </Text>
            </div>

            {/* Discount */}
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Text>Discount Amount:</Text>
              <Text>
                {numberFormat(isIndividual ? totals.discountTotal : data.total_discount)}
              </Text>
            </div>

            {/* VAT */}
            {/* {isIndia ? (
              <>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text>CGST :</Text>
                  <Text>{numberFormat(cgst)}</Text>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text>SGST :</Text>
                  <Text>{numberFormat(sgst)}</Text>
                </div>
              </>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>{taxLabel} Amount :</Text>
                <Text>{numberFormat(totalTax)}</Text>
              </div>
            )} */}

            {!isGSTDisabled && (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Text>{taxLabel} Amount :</Text>
                <Text>{numberFormat(totalTax)}</Text>
              </div>
            )}


            {/* Additional Expense Lines */}
            {(data.otherServiceTrans || []).length > 0 && (
              <div style={{ marginTop: 4 }}>
                {(data.otherServiceTrans || []).map((t, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "2px 0",
                      fontSize: "13px"
                    }}
                  >
                    <Text style={{ opacity: 0.85 }}>{t.to_acc_name.trim()} :</Text>
                    <Text style={{ opacity: 0.85 }}>{numberFormat(t.tran_amount)}</Text>
                  </div>
                ))}
              </div>
            )}


            {/* Grand Total */}
            <div
              style={{
                borderTop: "1px solid #000",
                display: "flex",
                justifyContent: "space-between",
                paddingTop: 4,
                marginTop: 4,
              }}
            >
              <Text strong>Grand Total ({institution?.currency}): </Text>
              <Text strong>{numberFormat(grandTotal)}</Text>
            </div>
          </div>
        </Col>
      </Row>

      <Divider />

      <Row>
        <Col span={24}>
          <Text>
            In Word of Grand Total :{" "}
            {convertNumberToWords(grandTotal, data?.currency || institution?.currency)}
          </Text>
          <br />
          <Text>Description : {data.narration || "-"}</Text>
        </Col>
      </Row>

      {(showStamp || showSignature) && (
        <div className="invoice-footer-section">

          <div className="footer-right-group">
            {showStamp && institution?.stamp && (
              <div className="stamp-block">
                <img src={institution.stamp} alt="Stamp" />
              </div>
            )}

            {showSignature && institution?.signature && (
              <div className="signature-block">
                <img src={institution.signature} alt="Signature" />
                <div className="sign-line-text">Authorized Signature</div>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

export default SalesOrderVoucherBody;
